import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Debounce window for Craft history: one undo step per typing burst. */
export const DEBOUNCED_FIELD_COMMIT_MS = 1000;

/**
 * Local controlled value with debounced commits to the parent (e.g. Craft `setProp`).
 * Flushes on blur and unmount so pending edits are not lost.
 */
export function useDebouncedFieldCommit(
  committedValue: string,
  onCommit: (value: string) => void,
  delayMs = DEBOUNCED_FIELD_COMMIT_MS
) {
  const [localValue, setLocalValue] = useState(committedValue);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const debouncedCommit = useMemo(
    () =>
      debounce((next: string) => {
        onCommitRef.current(next);
      }, delayMs),
    [delayMs]
  );

  useEffect(() => {
    setLocalValue(committedValue);
    debouncedCommit.cancel();
  }, [committedValue, debouncedCommit]);

  useEffect(
    () => () => {
      debouncedCommit.flush();
    },
    [debouncedCommit]
  );

  const onChange = useCallback(
    (next: string) => {
      setLocalValue(next);
      debouncedCommit(next);
    },
    [debouncedCommit]
  );

  const onBlur = useCallback(() => {
    debouncedCommit.flush();
  }, [debouncedCommit]);

  return { localValue, onChange, onBlur };
}
